import { FetchResult, MaybeMasked } from "@apollo/client"
import { flattenEdges, usePaginator } from "@pathwright/utils"
import produce from "immer"
import isEmpty from "lodash/isEmpty"
import throttle from "lodash/throttle"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import {
  CreateDiscussionMutationVariables,
  CreateDiscussionResponseLikeMutationResult,
  CreateDiscussionResponseMutationVariables,
  CreateDiscussionSubscriptionMutationResult,
  DeleteDiscussionResponseLikeMutationResult,
  DeleteDiscussionResponseMutationResult,
  DeleteDiscussionSubscriptionMutationResult,
  DiscussionContextInput,
  DiscussionDocument,
  DiscussionPostFragment,
  DiscussionQueryResult,
  DiscussionQueryVariables,
  DiscussionResponseDocument,
  DiscussionResponseFragment,
  DiscussionResponseQueryResult,
  DiscussionResponseQueryVariables,
  ResponseEdge,
  UpdateDiscussionResponseMutationResult,
  UpdateUserDiscussionHistoryMutationResult,
  useCreateDiscussionMutation,
  useCreateDiscussionResponseLikeMutation,
  useCreateDiscussionResponseMutation,
  useCreateDiscussionSubscriptionMutation,
  useDeleteDiscussionResponseLikeMutation,
  useDeleteDiscussionResponseMutation,
  useDeleteDiscussionSubscriptionMutation,
  useDiscussionQuery,
  useDiscussionResponseQuery,
  useUpdateDiscussionResponseMutation,
  useUpdateUserDiscussionHistoryMutation
} from "../api/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"

type DiscussionContextType = {
  context: DiscussionContextInput
  loading: boolean
  post: DiscussionPostFragment | null
  postLoading: boolean
  postError?: Error
  response: DiscussionResponseFragment | null
  responseDeleted: boolean
  responses: DiscussionResponseFragment[]
  responsesLoading: boolean
  responsesError: Error | undefined
  responsesHasMore: boolean
  replyingTo: DiscussionResponseFragment | null
  editingResponse: DiscussionResponseFragment | null
  setUnreadResponses: (responses: DiscussionResponseFragment[]) => void
  unreadResponses: DiscussionResponseFragment[]
  markAllResponsesAsRead: () => void
  loadMoreResponses: () => void
  setReplyingTo: (response: DiscussionResponseFragment | null) => void
  setEditingResponse: (response: DiscussionResponseFragment | null) => void
  scrollToResponse: (response: DiscussionResponseFragment) => void
  scrollRef: React.RefObject<any>
  viewResponse: (response: DiscussionResponseFragment) => void
  viewAllResponses: () => void
  createDiscussion: (
    args: Pick<CreateDiscussionMutationVariables, "initialResponse"> &
      CreateDiscussionMutationVariables["post"]
  ) => Promise<void>
  updateUserDiscussionHistory: () => Promise<
    FetchResult<MaybeMasked<UpdateUserDiscussionHistoryMutationResult["data"]>>
  >
  subscribeToDiscussion: () => Promise<
    FetchResult<MaybeMasked<CreateDiscussionSubscriptionMutationResult["data"]>>
  >
  unsubscribeToDiscussion: () => Promise<
    FetchResult<MaybeMasked<DeleteDiscussionSubscriptionMutationResult["data"]>>
  >
  createResponse: (
    response: CreateDiscussionResponseMutationVariables["response"]
  ) => Promise<any>
  updateResponse: (
    response: DiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<UpdateDiscussionResponseMutationResult["data"]>>
  >
  deleteResponse: (
    response: DiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<DeleteDiscussionResponseMutationResult["data"]>>
  >
  likeResponse: (
    response: DiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<CreateDiscussionResponseLikeMutationResult["data"]>>
  >
  unlikeResponse: (
    response: DiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<DeleteDiscussionResponseLikeMutationResult["data"]>>
  >
}

const DiscussionContext = createContext<DiscussionContextType>(
  {} as DiscussionContextType
)
export const useDiscussionContext = () => useContext(DiscussionContext)

type DiscussionProviderProps = {
  id: number
  context: DiscussionContextInput
  response_id?: number
  initialResponse?: DiscussionResponseFragment
  sortResponses?: (
    responses: DiscussionResponseFragment[]
  ) => DiscussionResponseFragment[]
  orderResponsesBy?: string
  onViewResponse?: (response: DiscussionResponseFragment) => void
  onViewAllResponses?: () => void
  insertNewResponseEdge: (
    edges: ResponseEdge[],
    edge: ResponseEdge
  ) => ResponseEdge[]
  onDiscussionResponseCreated: (
    post: DiscussionPostFragment,
    response: DiscussionResponseFragment
  ) => void
  children: React.ReactNode
}

function DiscussionProvider(props: DiscussionProviderProps) {
  const {
    id,
    context,
    response_id,
    initialResponse,
    sortResponses,
    orderResponsesBy,
    onViewResponse,
    onViewAllResponses,
    insertNewResponseEdge,
    onDiscussionResponseCreated
  } = props

  const { me } = usePathwrightContext()

  const discussionQuery = useDiscussionQuery({
    variables: {
      id,
      context,
      orderResponsesBy
    },
    skip: !!response_id,
    fetchPolicy: "cache-and-network"
  })
  const { data: discussionData, ...discussionState } = discussionQuery

  const { data: responseData, ...responseState } = useDiscussionResponseQuery({
    variables: {
      id,
      context,
      response_id: response_id!
    },
    skip: !response_id
  })

  const post = discussionData?.discussion || null
  const queriedResponse = responseData?.discussion?.response || null

  const { hasMore, loadMore } = usePaginator({
    data: discussionQuery,
    path: "discussion.responses"
  })

  const [createDiscussionMutation, createDiscussionState] =
    useCreateDiscussionMutation()
  const [createResponseMutation] = useCreateDiscussionResponseMutation()
  const [updateResponseMutation] = useUpdateDiscussionResponseMutation()
  const [deleteResponseMutation] = useDeleteDiscussionResponseMutation()
  const [createResponseLikeMutation] = useCreateDiscussionResponseLikeMutation()
  const [deleteResponseLikeMutation] = useDeleteDiscussionResponseLikeMutation()
  const [updateUserDiscussionHistoryMutation] =
    useUpdateUserDiscussionHistoryMutation()
  const [subscribeToDiscussionMutation] =
    useCreateDiscussionSubscriptionMutation()
  const [unsubscribeToDiscussionMutation] =
    useDeleteDiscussionSubscriptionMutation()

  const [loading, setLoading] = useState(false)
  const [replyingTo, setReplyingTo] =
    useState<DiscussionResponseFragment | null>(null)
  const [editingResponse, setEditingResponse] =
    useState<DiscussionResponseFragment | null>(null)
  const [response, setResponse] = useState<DiscussionResponseFragment | null>(
    null
  )
  const [unreadResponses, setUnreadResponses] = useState<
    DiscussionResponseFragment[]
  >([])

  const scrollRef = useRef<any>()

  type SortedResponse = DiscussionResponseFragment & {
    responses: SortedResponse[]
  }
  const responses = flattenEdges(
    discussionData?.discussion?.responses
  ) as SortedResponse[]
  const sortedResponses = useMemo(() => {
    let sorted: SortedResponse[] = []
    if (initialResponse) sorted.push({ ...initialResponse, responses: [] })
    if (responses) {
      const lookup: Record<string, SortedResponse> = {}
      const tree: SortedResponse[] = []
      responses.forEach((r) => (lookup[r.id] = { ...r, responses: [] }))
      responses.forEach((r) => {
        if (r.parent_id) {
          const parent = lookup[r.parent_id]
          if (parent) parent.responses.push(lookup[r.id])
        } else {
          tree.push(lookup[r.id])
        }
      })
      function flatten(r: SortedResponse) {
        sorted.push(r)
        r.responses.forEach(flatten)
      }
      tree.forEach(flatten)
    }
    return sortResponses?.(sorted) || sorted
  }, [initialResponse, responses, sortResponses])

  useEffect(() => {
    if (!responseState.loading && !discussionState.loading) setLoading(false)
  }, [responseState.loading, discussionState.loading])

  useEffect(() => setResponse(queriedResponse), [queriedResponse])

  useEffect(() => {
    if (replyingTo) scrollToResponse(replyingTo)
    if (editingResponse) scrollToResponse(editingResponse)
  }, [replyingTo, editingResponse])

  useEffect(() => {
    if (post && !isEmpty(post) && responses && responses.length) {
      updateUserDiscussionHistory()
    }
  }, [!!post])

  const markAllResponsesAsRead = useCallback(
    throttle(() => {
      setTimeout(() => setUnreadResponses([]), 1000)
    }, 1000),
    []
  )

  async function loadMoreResponses() {
    if (hasMore && !loading) {
      try {
        setLoading(true)
        await loadMore()
      } finally {
        setLoading(false)
      }
    }
  }

  function scrollToResponse(r: DiscussionResponseFragment) {
    const index = sortedResponses.findIndex((x) => x.id === r.id)
    if (scrollRef.current) {
      scrollRef.current.scrollToIndex({
        index: index !== -1 ? index : 0,
        viewOffset: 0,
        viewPosition: 0.3
      })
    }
  }

  function viewResponse(r: DiscussionResponseFragment) {
    setResponse(r)
    onViewResponse?.(r)
  }

  function viewAllResponses() {
    setResponse(null)
    onViewAllResponses?.()
  }

  // Mutations wiring
  const createDiscussion: DiscussionContextType["createDiscussion"] = async ({
    title,
    body,
    type,
    initialResponse
  }) => {
    await createDiscussionMutation({
      variables: {
        context,
        post: { title, body, type },
        initialResponse
      },
      refetchQueries: ["Discussion"]
    })
  }

  const updateResponse = (response: DiscussionResponseFragment) =>
    updateResponseMutation({
      variables: {
        context,
        response_id: response.id,
        response: {
          body: response.body,
          attachments: response.attachments,
          discussion_id: post!.id
        }
      },
      optimisticResponse: {
        updateDiscussionResponse: { ...response, __typename: "Response" }
      }
    })

  const deleteResponse = (response: DiscussionResponseFragment) =>
    deleteResponseMutation({
      variables: {
        response_id: response.id,
        discussion_id: post!.id,
        context
      },
      update: (cache) => {
        if (response_id) {
          const query = DiscussionResponseDocument
          const variables = {
            id,
            response_id: response_id,
            context
          }
          const data = cache.readQuery<
            DiscussionResponseQueryResult["data"],
            DiscussionResponseQueryVariables
          >({ query, variables })
          const nextData = produce(data, (draft) => {
            if (draft?.discussion) {
              draft.discussion.response = null
            }
          })
          cache.writeQuery({ query, variables, data: nextData })
        } else {
          const query = DiscussionDocument
          const variables = {
            id,
            context,
            orderResponsesBy
          }
          const data = cache.readQuery<
            DiscussionQueryResult["data"],
            DiscussionQueryVariables
          >({ query, variables })
          const nextData = {
            discussion: {
              ...data?.discussion,
              total_responses: (data?.discussion?.total_responses || 0) - 1,
              responses: {
                ...data?.discussion?.responses,
                total: (data?.discussion?.responses?.total || 0) - 1,
                edges: data?.discussion?.responses?.edges?.filter(
                  (edge) => edge?.node?.id !== response.id
                )
              }
            }
          }
          cache.writeQuery({ query, variables, data: nextData })
        }
      }
    })

  const likeResponse = (response: DiscussionResponseFragment) =>
    createResponseLikeMutation({
      variables: {
        response_id: response.id,
        discussion_id: post!.id,
        context
      }
    })
  const unlikeResponse = (response: DiscussionResponseFragment) =>
    deleteResponseLikeMutation({
      variables: {
        response_id: response.id,
        discussion_id: post!.id,
        context
      },
      optimisticResponse: {
        __typename: "Mutations",
        deleteDiscussionResponseLike: true
      },
      update: (cache) => {
        if (response_id) {
          const query = DiscussionResponseDocument
          const variables = {
            id,
            response_id: response_id,
            context
          }
          const data = cache.readQuery<
            DiscussionResponseQueryResult["data"],
            DiscussionResponseQueryVariables
          >({ query, variables })
          const nextData = produce(data, (draft) => {
            if (draft?.discussion?.response) {
              draft.discussion.response.user_liked = false
              draft.discussion.response.likes =
                (draft.discussion.response.likes || 0) - 1
            }
          })
          cache.writeQuery({ query, variables, data: nextData })
        } else {
          const query = DiscussionDocument
          const variables = {
            id,
            context,
            orderResponsesBy
          }
          const data = cache.readQuery<
            DiscussionQueryResult["data"],
            DiscussionQueryVariables
          >({ query, variables })
          const nextData = produce(data, (draft) => {
            const edge = draft?.discussion?.responses?.edges?.find(
              (edge) => edge?.node?.id === response.id
            )
            if (edge?.node) {
              edge.node.user_liked = false
              edge.node.likes = (edge.node.likes || 0) - 1
            }
          })
          cache.writeQuery({ query, variables, data: nextData })
        }
      }
    })

  const updateUserDiscussionHistory = () =>
    updateUserDiscussionHistoryMutation({
      variables: { context, discussion_id: post!.id },
      optimisticResponse: {
        updateUserDiscussionHistory: {
          ...post!,
          user_last_viewed_time: new Date().toISOString(),
          user_has_viewed_recent: true,
          __typename: "Discussion"
        }
      }
    })

  const subscribeToDiscussion = () =>
    subscribeToDiscussionMutation({
      variables: { context, discussion_id: post!.id }
    })
  const unsubscribeToDiscussion = () =>
    unsubscribeToDiscussionMutation({
      variables: { context, discussion_id: post!.id }
    })

  const createResponse = (
    responseVariables: CreateDiscussionResponseMutationVariables["response"]
  ) =>
    createResponseMutation({
      variables: {
        context,
        response: {
          ...responseVariables,
          discussion_id: post?.id!
        }
      },
      optimisticResponse: {
        createDiscussionResponse: {
          id: `discussion-response-optimistic-${new Date().getTime()}` as unknown as number,
          parent_id: null,
          body: responseVariables.body,
          attachments: null,
          likes: 0,
          user_liked: null,
          created_time: new Date().toISOString(),
          user: me,
          __typename: "Response"
        }
      },

      update: (cache, result) => {
        const query = DiscussionDocument
        const variables = {
          id,
          context,
          orderResponsesBy
        }
        const data = cache.readQuery<
          DiscussionQueryResult["data"],
          DiscussionQueryVariables
        >({ query, variables })
        const edge = {
          node: result.data?.createDiscussionResponse,
          __typename: "ResponseEdge"
        } as ResponseEdge
        const nextData = {
          discussion: {
            ...data?.discussion,
            total_responses: (data?.discussion?.total_responses || 0) + 1,
            user_is_subscribed: true,
            responses: {
              ...data?.discussion?.responses,
              total: (data?.discussion?.responses?.total || 0) + 1,
              edges: insertNewResponseEdge
                ? insertNewResponseEdge(
                    (data?.discussion?.responses?.edges as ResponseEdge[]) ||
                      [],
                    edge
                  )
                : [...(data?.discussion?.responses?.edges || []), edge]
            }
          }
        }
        cache.writeQuery({ query, variables, data: nextData })
      },
      onCompleted: ({ createDiscussionResponse }) =>
        onDiscussionResponseCreated?.(post!, createDiscussionResponse!)
    })

  const discussionContext = {
    context,
    loading:
      discussionState.loading ||
      responseState.loading ||
      createDiscussionState.loading ||
      loading,
    post: post,
    postLoading: discussionState.loading || loading,
    postError: discussionState.error,
    response: response,
    responseDeleted: responseState.loading
      ? false
      : !!response_id && !queriedResponse,
    responses: sortedResponses,
    responsesLoading: discussionState.loading || loading,
    responsesError: responseState.error,
    responsesHasMore: hasMore,
    replyingTo,
    editingResponse,
    setUnreadResponses,
    unreadResponses,
    markAllResponsesAsRead,
    loadMoreResponses,
    setReplyingTo,
    setEditingResponse,
    scrollToResponse,
    scrollRef,
    viewResponse,
    viewAllResponses,
    createDiscussion,
    updateUserDiscussionHistory,
    subscribeToDiscussion,
    unsubscribeToDiscussion,
    createResponse,
    updateResponse,
    deleteResponse,
    likeResponse,
    unlikeResponse
  }

  return (
    <DiscussionContext.Provider value={discussionContext}>
      {props.children}
    </DiscussionContext.Provider>
  )
}

export default DiscussionProvider
